import type {
  CalendarEvent,
  IMediaFile,
  InvokeTranscribeResult,
  MeetingDisplay,
  Memory,
  StoredMediaFile,
  ThreadConversation,
  TimeData,
  Transcript,
  TranscriptFull,
  TranscriptOutput,
  TranscriptOutputLineParsedTime,
  TranscriptOutputLineRawTime,
  TranscriptSummary
} from '@repo/common';
import type { AssistantContext } from '@repo/common/types/AssistantMachine';
import { MissingTranscriptError } from '@repo/common/types/Transcript';
import { formatTime, getDateStrLocal } from './date.util';
import {
  convertToTranscriptionOutput,
  formatVttOutput,
  msToTimeCode,
  parsedToRawTime,
  parseRawOutput
} from './timecode';
import type { SegmentResult } from '@repo/common/types/Audio';

// Only exported for testing purposes
export const addOffsetTranscribeResult = (
  transcriptData: InvokeTranscribeResult,
  offset: number
) => {
  return transcriptData.transcriptions
    .map((line, idx) => {
      try {
        const output = parseRawOutput(line, idx, true);
        const startSeconds = output.startSeconds + offset;
        const endSeconds = output.endSeconds + offset;

        const startSecondsStr = msToTimeCode(startSeconds * 1000);
        const endSecondsStr = msToTimeCode(endSeconds * 1000);

        return convertToTranscriptionOutput(startSecondsStr, endSecondsStr, output.text);
      } catch (error) {
        console.log('Error parsing transcript line', error);
        return '';
      }
    })
    .filter((x) => x !== '');
};

/**
 * Utility: determine if a new batch of raw transcriptions overlaps with existing raw output.
 * Assumes both arrays are already in chronological order by start time.
 * Returns true if first new startSeconds <= max existing endSeconds.
 */
export function isOverlappingExisting(existingRaw: string[], newRaw: string[]): boolean {
  if (!existingRaw || existingRaw.length === 0) return false;
  if (!newRaw || newRaw.length === 0) return false;
  try {
    const lastExisting = parseRawOutput(
      existingRaw[existingRaw.length - 1],
      existingRaw.length - 1,
      true
    );
    const firstNew = parseRawOutput(newRaw[0], 0, true);
    const isOverlap = firstNew.startSeconds < lastExisting.endSeconds;
    if (isOverlap) {
      console.warn('[Transcribe] Overlap detected, discarding new raw output', {
        firstNewStart: firstNew.startSeconds,
        firstNew: firstNew.vtt,
        lastExistingEnd: lastExisting.endSeconds,
        lastExisting: lastExisting.vtt
      });
    }
    return isOverlap;
  } catch (e) {
    console.debug('[Transcribe] Overlap check failed, ignoring overlap guard', e);
    return false;
  }
}

// Gets the valid existing lines for the transcription window.
export const getValidNewLines = (startingCutoff: number, my: string[]): string[] => {
  // Only keep data before the rolling transcribe window
  let myTranscriptOutput: TranscriptOutput;
  try {
    myTranscriptOutput = my.map(parseRawWithTiming).filter((x) => x !== undefined);
  } catch (err) {
    console.error('Could not get transcript my lines', err);
    return [];
  }

  return myTranscriptOutput
    .map((t, index) => ({ t, index }))
    .filter(({ t }) => t.startSeconds >= startingCutoff)
    .map(({ index }) => my[index]);
};

// Gets the valid existing lines for the transcription window.
export const getValidExistingLines = (
  startingCutoff: number,
  toleranceSeconds: number,
  existing: string[]
): string[] => {
  // Only keep data before the rolling transcribe window
  let existingTranscriptOutput: TranscriptOutput;
  try {
    existingTranscriptOutput = existing.map(parseRawWithTiming).filter((x) => x !== undefined);
  } catch (err) {
    console.error('Could not get transcript existing lines', err);
    return [];
  }

  console.log(
    'Kept lines',
    existingTranscriptOutput
      .map((t, index) => ({ t, index }))
      .filter(({ t }) => t.endSeconds <= startingCutoff + toleranceSeconds)
      .map(({ index }) => existing[index])
      .join('\n')
  );

  console.log(
    'Discarded lines (too new): ',
    existingTranscriptOutput
      .map((t, index) => ({ t, index }))
      .filter(({ t }) => t.endSeconds > startingCutoff + toleranceSeconds)
      .map(({ index }) => existing[index])
      .join('\n')
  );

  return existingTranscriptOutput
    .map((t, index) => ({ t, index }))
    .filter(({ t }) => t.endSeconds <= startingCutoff + toleranceSeconds)
    .map(({ index }) => existing[index]);
};

export const mergeExistingTranscripts = (
  startSeconds: number, // indicates the starting point for which the output is canonical
  appendOnly: boolean, // flag of whether we should keep all existing transcriptions and append only
  existing: string[], // the existing transcriptions
  newLines: string[], // the new transcriptions
  toleranceSeconds: number = 10
): string[] => {
  const existingLines = appendOnly
    ? existing
    : getValidExistingLines(startSeconds, toleranceSeconds, existing);

  console.log('New lines: ', newLines.join('\n'));

  return [...existingLines, ...newLines];
};

const parseRawWithTiming = (
  raw: string,
  index: number
): TranscriptOutputLineParsedTime | undefined => {
  try {
    return parseRawOutput(raw, index, true);
  } catch (error) {
    console.log('Error parsing transcript line', error);
    return undefined;
  }
};

export const appendTranscribeResult = async (
  startTm: number,
  startSeconds: number,
  transcriptData: InvokeTranscribeResult,
  context: AssistantContext,
  updateTranscript: (id: string, partialUpdate: Partial<TranscriptFull>) => Promise<void>
): Promise<Pick<TranscriptFull, 'rawOutput' | 'editedOutput'>> => {
  if (!context.transcript) {
    throw new MissingTranscriptError('unknown', 'Cannot transcribe when missing transcript');
  }
  if (!transcriptData.transcriptions) {
    throw new MissingTranscriptError('unknown', 'Missing transcriptions data in transcriptData');
  }

  const runTm = Date.now() - startTm;
  console.debug(`Invoke transcribe (@${startSeconds}s) duration: ${runTm}ms`);
  console.log(
    `Prompt: ${context.prompt}, Transcribe raw output: ${[...transcriptData.transcriptions].join('\n')}`
  );

  const transcript = context.transcript;

  // Overlap guard for append case: use transcript.rawOutput
  if (
    transcript.rawOutput &&
    transcript.rawOutput.length > 0 &&
    isOverlappingExisting(transcript.rawOutput, transcriptData.transcriptions)
  ) {
    console.warn('[Transcribe] Discarding append due to overlap (double processing likely)');
    return transcript;
  }

  transcript.status = 'transcribed';

  transcript.rawOutput = [...transcript.rawOutput, ...transcriptData.transcriptions];
  transcript.editedOutput = transcript.rawOutput;
  transcript.output = generateTranscriptOutput(transcript, true);

  await updateTranscript(transcript.id, {
    rawOutput: transcript.rawOutput,
    editedOutput: transcript.editedOutput,
    output: transcript.output,
    status: transcript.status,
    duration: startSeconds
  });

  return transcript;
};

export const overwriteTranscribeResult = async (
  transcriptData: InvokeTranscribeResult,
  context: AssistantContext,
  updateTranscript: (id: string, partialUpdate: Partial<TranscriptFull>) => Promise<void>
): Promise<Pick<TranscriptFull, 'rawOutput' | 'editedOutput'>> => {
  if (!context.transcript) {
    throw new MissingTranscriptError('unknown', 'Cannot transcribe when missing transcript');
  }
  if (!transcriptData.transcriptions) {
    throw new MissingTranscriptError('unknown', 'Missing transcriptions data in transcriptData');
  }

  const output = transcriptData.transcriptions;

  const outputParsed = output.map((obj, i) => parseRawOutput(obj, i, true));
  if (outputParsed.length === 0) {
    return context.transcript;
  }

  const startSecondsParsed = outputParsed[0].startSeconds;
  const existing = getValidExistingLines(startSecondsParsed, 0, context.transcript.rawOutput);
  const transcript = context.transcript;
  transcript.status = 'transcribed';
  transcript.rawOutput = [...existing, ...output];
  transcript.editedOutput = [...transcript.rawOutput];

  await updateTranscript(transcript.id, {
    rawOutput: transcript.rawOutput,
    editedOutput: transcript.editedOutput,
    output: transcript.output,
    status: transcript.status,
    duration: startSecondsParsed
  });

  return transcript;
};

export const processTranscribeResult = async (
  startTm: number,
  startSeconds: number,
  existing: string[],
  transcriptData: InvokeTranscribeResult,
  context: AssistantContext,
  updateTranscript: (id: string, partialUpdate: Partial<TranscriptFull>) => Promise<void>
): Promise<Pick<TranscriptFull, 'rawOutput' | 'editedOutput'>> => {
  if (!context.transcript) {
    throw new MissingTranscriptError('unknown', 'Cannot transcribe when missing transcript');
  }
  if (!transcriptData.transcriptions) {
    throw new MissingTranscriptError('unknown', 'Missing transcriptions data in transcriptData');
  }

  const runTm = Date.now() - startTm;
  console.debug(`Invoke transcribe (@${startSeconds}s) duration: ${runTm}ms`);

  const output = transcriptData.transcriptions;

  const outputParsed = output.map((obj, i) => parseRawOutput(obj, i, true));
  let startSecondsParsed = startSeconds;
  if (outputParsed.length) {
    startSecondsParsed = outputParsed[0].startSeconds;
  }

  const transcript = context.transcript;

  transcript.status = 'transcribed';

  // If we marked the previous transcription progress, then append only.

  transcript.rawOutput = mergeExistingTranscripts(startSecondsParsed, false, existing, output);
  transcript.editedOutput = [...transcript.rawOutput];

  await updateTranscript(transcript.id, {
    rawOutput: transcript.rawOutput,
    editedOutput: transcript.editedOutput,
    output: transcript.output,
    status: transcript.status,
    duration: startSeconds
  });

  return transcript;
};

export const finalizeTranscriptionUpdates = (context: AssistantContext) => {
  if (!context.transcript) {
    throw new MissingTranscriptError('unknown', 'Cannot transcribe when missing transcript');
  }
  const transcript = context.transcript;

  if (transcript.rawOutput.length >= 1) {
    const lastItem = transcript.rawOutput[transcript.rawOutput.length - 1];
    const lastItemParsed = parseRawOutput(lastItem, 0, true);

    // Update the progress to our last completed transcription
    console.log('Updated transcribe progress:', lastItemParsed.endSeconds);
    context.transcribeProgress = lastItemParsed.endSeconds;
  }
};

const getSpeakerKey = (line: TranscriptOutputLineRawTime) => `${line.start}-${line.end}`;

// Multiple function overloads
export function generateTranscriptOutput(
  transcript: Pick<TranscriptFull, 'output' | 'rawOutput'>,
  parseTime: false
): TranscriptOutputLineRawTime[];
export function generateTranscriptOutput(
  transcript: Pick<TranscriptFull, 'output' | 'rawOutput'>,
  parseTime: true
): TranscriptOutputLineParsedTime[];

export function generateTranscriptOutput(
  transcript: Pick<TranscriptFull, 'output' | 'rawOutput'>,
  parseTime: true | false
): TranscriptOutputLineRawTime[] | TranscriptOutputLineParsedTime[] {
  const speakerLookup = getExistingSpeakerLookup(transcript.output || []);

  const doParse = (raw: string, index: number) => {
    raw = raw.replaceAll('<|endoftext|>', '');
    try {
      return parseRawOutput(raw, index, true);
    } catch (error) {
      console.log('Error parsing transcript line', error);
      return undefined;
    }
  };

  const parsedFlat = toFlatSentenceChunks(
    transcript.rawOutput.map(doParse).filter((x) => x !== undefined)
  );

  const parsedFlatSpeaker = parsedFlat.map((line) => {
    // Restore speaker information if available
    line.speaker = speakerLookup[getSpeakerKey(parsedToRawTime(line))];
    return line;
  });

  if (!parseTime) {
    return parsedFlatSpeaker.map((line) => parsedToRawTime(line));
  }

  return parsedFlatSpeaker;
}

export function getExistingSpeakerLookup(output: TranscriptOutput) {
  // Extract speaker information from lines into hashmap

  const speakerLookup: Record<string, TranscriptOutputLineParsedTime['speaker']> = {};
  for (const line of output || []) {
    speakerLookup[getSpeakerKey(parsedToRawTime(line))] = line.speaker;
  }
  return speakerLookup;
}

export function getTranscriptOutput(
  transcript: TranscriptFull,
  forceRefresh = false
): TranscriptOutput {
  // TODO: Improve how the need to refresh is detected, e.g. store a hashmap
  if (!transcript.rawOutput || !transcript.rawOutput.length) {
    return [];
  }

  const shouldRefresh =
    forceRefresh ||
    !transcript.output ||
    !transcript.output.length ||
    transcript.output.length != transcript.rawOutput.length;
  if (shouldRefresh) {
    transcript.output = generateTranscriptOutput(transcript, true);
  }
  return transcript.output || [];
}

export function sentenceSegmentOutput(
  input: TranscriptOutputLineParsedTime[]
): TranscriptOutputLineParsedTime[] {
  return toFlatSentenceChunks(input);
}

const marker = '|#|';
const delimiter = [marker, '.', '!', '?'];

function getHasMarker(needle: string) {
  return (x: string) => x.endsWith(needle);
}
type HasMarker = (x: string) => boolean;
const hasDelimiters: HasMarker[] = delimiter.map(getHasMarker);

export const hasDelimiter = (x: string) => hasDelimiters.some((y: HasMarker) => y(x));

export function flattenSentenceChunk(
  input: TranscriptOutputLineParsedTime[]
): TranscriptOutputLineParsedTime {
  // Ensure chronological ordering when computing boundaries
  // Do NOT mutate original input order (used for text concatenation)
  const sorted = input.slice().sort((a, b) => a.startSeconds - b.startSeconds);
  const first = sorted[0];
  const last = sorted[sorted.length - 1];

  const flattened = {
    ...first,
    end: last.end,
    endSeconds: last.endSeconds,
    text: input
      .map((x) => x.text)
      .join('')
      .replaceAll(marker, ''),
    vtt: ''
  };
  flattened.vtt = formatVttOutput(flattened.start, flattened.end, flattened.text);
  return flattened;
}

export function toFlatSentenceChunks(
  input: TranscriptOutputLineParsedTime[],
  softMax: number = 2,
  hardMax: number = 50
): TranscriptOutputLineParsedTime[] {
  let currentIndex = 0;
  const chunks: TranscriptOutputLineParsedTime[] = [];
  while (currentIndex < input.length) {
    let endIndex = Math.min(currentIndex + softMax, input.length);
    // Find the next period after the softMax
    const periodIndex = input.findIndex((x, i) => i >= endIndex && hasDelimiter(x.text));
    endIndex = periodIndex == -1 ? input.length : periodIndex + 1;
    endIndex = Math.min(currentIndex + hardMax, endIndex);
    const chunk = input.slice(currentIndex, endIndex);
    chunks.push(flattenSentenceChunk(chunk));
    currentIndex = endIndex;
  }
  return chunks;
}

/**
 * Removes duplicate n-grams (for n = 3, 2, and 1) from the input text.
 * @param text - The input string to process.
 * @returns The processed string with duplicate n-grams removed.
 */
export function removeDuplicateNgrams(text: string): string {
  // Tokenize the input text by whitespace.
  let tokens: string[] = text.trim().split(/\s+/);

  // Process larger n-grams first to catch longer duplicate patterns.
  [3, 2, 1].forEach((n) => {
    tokens = removeDuplicateForN(tokens, n);
  });

  return tokens.join(' ');
}

/**
 * Helper function that removes adjacent duplicate n-grams from a token array.
 * @param tokens - An array of string tokens.
 * @param n - The size of the n-gram.
 * @returns A new array of tokens with adjacent duplicate n-grams removed.
 */
export function removeDuplicateForN(tokens: string[], n: number): string[] {
  const result: string[] = [];
  let i: number = 0;

  while (i < tokens.length) {
    // Ensure there are at least two n-grams available to compare.
    if (i + 2 * n <= tokens.length) {
      const ngram1: string = tokens.slice(i, i + n).join(' ');
      const ngram2: string = tokens.slice(i + n, i + 2 * n).join(' ');

      // If the adjacent n-grams are identical, skip the duplicate segment.
      if (ngram1 === ngram2) {
        i += n;
        continue;
      }
    }
    result.push(tokens[i]);
    i++;
  }

  return result;
}

export function getTranscriptName(transcript: Transcript | undefined) {
  if (!transcript) {
    return '';
  }
  return transcript.editedName || transcript.name || transcript.id;
}

export function getTranscriptDate(transcript: Transcript | undefined) {
  if (!transcript) {
    return new Date();
  }
  return transcript.date || new Date();
}

export const memoryToMeetingDisplay = (
  calendarEvent: CalendarEvent,
  transcript: Transcript
): MeetingDisplay => {
  return {
    id: calendarEvent.uid || '',
    transcript: transcript,
    name: transcript.name || '',
    email: calendarEvent?.attendees?.[0]?.email || '',
    subject: calendarEvent.title || '',
    text: transcript.summary || '',
    date: new Date(calendarEvent.start || 0).toISOString(),
    read: false,
    labels: ['work', 'budget', 'personal'],
    nodes: []
  };
};

export const threadToMeetingDisplay = (thread: ThreadConversation): MeetingDisplay => {
  return {
    id: thread.conversationId,
    name: thread.subject || '',
    email: '',
    subject: thread.subject || '',
    text: thread.summary || '',
    date: new Date(thread.date || 0).toISOString(),
    read: false,
    labels: [],
    nodes: thread.nodes
  };
};
export const getTranscriptionLabel = (speaker: string | undefined) => {
  if (!speaker) {
    return '';
  }
  return `${speaker}: `;
};

const getTranscriptLines = (output: TranscriptFull['output']): TranscriptOutputLineParsedTime[] => {
  if (!output) return [];
  if (typeof output === 'string') {
    try {
      const parsed = JSON.parse(output);
      return Array.isArray(parsed) ? (parsed as TranscriptOutputLineParsedTime[]) : [];
    } catch (error) {
      console.error('Error parsing transcript output string', error);
      return [];
    }
  }
  if (Array.isArray(output)) {
    return output as TranscriptOutputLineParsedTime[];
  }
  return [];
};

export const transcriptToMarkdown = (transcript: TranscriptFull): string => {
  let transcriptMarkdown = '';
  transcriptMarkdown += `# Meeting\n\n`;
  transcriptMarkdown += `**Date**: ${new Date(transcript.createdAt || 0).toISOString()}\n`;
  transcriptMarkdown += `**Name**: ${transcript.name}\n`;

  const outputLines = getTranscriptLines(transcript.output);
  if (outputLines.length > 0) {
    transcriptMarkdown += `**Transcription**:\n\n`;
    try {
      transcriptMarkdown += outputLines
        .map((line) => `${getTranscriptionLabel(line.speaker)}${line.text}`)
        .join('\n\n');
    } catch (error) {
      console.error('Error preparing summary input', error);
    }
  }

  if (transcript.userNotes) {
    transcriptMarkdown += `\n\n## User Notes\n\n`;
    transcriptMarkdown += transcript.userNotes;
  }

  return transcriptMarkdown;
};

/**
 * Parses raw transcript output lines into a working string format.
 * Preserves sentences and |#| delimiters for further processing.
 *
 * @param rawOutput - Array of raw transcript output lines
 * @returns Combined string with sentences and delimiters
 */
export const parseRawOutputToWorkingString = (rawOutput: string[]): string => {
  if (!rawOutput || rawOutput.length === 0) {
    return '';
  }

  const tokens = rawOutput
    .map((output, index) => {
      try {
        return parseRawOutput(output, index, false).text;
      } catch (error) {
        console.warn('Failed to parse raw output line:', error);
        return '';
      }
    })
    .filter((text) => text.length > 0);

  return tokens.join('');
};

/**
 * Creates a sentence-aware rolling window of transcript text for prompts.
 * Truncates to soft max of 100 tokens while preserving sentences, with hard limit of 500 tokens.
 *
 * @param rawOutput - Array of raw transcript output lines
 * @param softMaxTokens - Soft maximum tokens (default: 100)
 * @param hardMaxTokens - Hard maximum tokens (default: 500)
 * @returns Truncated text maintaining sentence boundaries
 */
export const createSentenceAwarePrompt = (
  rawOutput: string[],
  softMaxTokens: number = 100,
  hardMaxTokens: number = 500
): string => {
  if (!rawOutput || rawOutput.length === 0) {
    return '';
  }

  // Parse into working string format
  const workingString = parseRawOutputToWorkingString(rawOutput);
  if (!workingString) {
    return '';
  }

  // Split the whole string into tokens FIRST: words or delimiter characters
  const tokens: string[] = [];
  const parts = workingString.split(/(\s+|\|#\||[.!?]+)/).filter((part) => part.length > 0);

  for (const part of parts) {
    if (/^\s+$/.test(part)) {
      // Skip whitespace tokens
      continue;
    } else if (/^(\|#\||[.!?]+)$/.test(part)) {
      // This is a delimiter
      tokens.push(part);
    } else {
      // This is text, split by spaces and add individual words
      const words = part.split(/\s+/).filter((word) => word.length > 0);
      tokens.push(...words);
    }
  }

  if (tokens.length === 0) {
    return '';
  }

  // Find delimiter token indices
  const delimiterTokenIndex: number[] = [];
  for (let i = 0; i < tokens.length; i++) {
    if (/^(\|#\||[.!?]+)$/.test(tokens[i])) {
      delimiterTokenIndex.push(i);
    }
  }

  // Add final token index if not already a delimiter
  if (
    delimiterTokenIndex.length === 0 ||
    delimiterTokenIndex[delimiterTokenIndex.length - 1] !== tokens.length - 1
  ) {
    delimiterTokenIndex.push(tokens.length - 1);
  }

  // Calculate token counts for each sentence (between delimiters)
  const sentenceTokenCounts: number[] = [];
  let prevDelimiter = -1;
  for (const delimiterIndex of delimiterTokenIndex) {
    const sentenceLength = delimiterIndex - prevDelimiter;
    sentenceTokenCounts.push(sentenceLength);
    prevDelimiter = delimiterIndex;
  }

  // Create cumulative sums from the end (most recent sentences)
  const reverseCumSums: number[] = [];
  let cumSum = 0;
  for (let i = sentenceTokenCounts.length - 1; i >= 0; i--) {
    cumSum += sentenceTokenCounts[i];
    reverseCumSums.unshift(cumSum);
  }

  // Find cut point based on soft max
  // We want the largest group that fits within softMaxTokens
  let i = reverseCumSums.length - 1; // Start from smallest group (most recent sentence)
  while (i > 0 && reverseCumSums[i] < softMaxTokens) {
    i--; // Move to larger groups
  }

  // If even the smallest group exceeds softMax, take the smallest anyway
  if (i === reverseCumSums.length - 1 && reverseCumSums[i] > softMaxTokens) {
    // Keep i as is - take the most recent sentence
  }

  const cumsum = reverseCumSums[i];

  // Apply hard max constraint - this overrides sentence boundaries
  let cut = cumsum;
  let needsTokenCutting = false;
  if (cumsum > hardMaxTokens) {
    cut = hardMaxTokens;
    needsTokenCutting = true;
  }

  // Get selected tokens, applying hard max constraint if needed
  let selectedTokens = tokens.slice(-cut).filter((token) => token !== '|#|');

  if (needsTokenCutting) {
    // For hard max, count only word tokens (not punctuation)
    const wordTokens = selectedTokens.filter((token) => !/^[.!?]$/.test(token));
    if (wordTokens.length > hardMaxTokens) {
      // Take the last N word tokens
      const lastWordTokens = wordTokens.slice(-hardMaxTokens);

      // Rebuild the token array with punctuation that comes after the last word
      const lastWordIndex = selectedTokens.lastIndexOf(lastWordTokens[lastWordTokens.length - 1]);
      const startIndex = selectedTokens.indexOf(lastWordTokens[0]);
      selectedTokens = selectedTokens.slice(startIndex, lastWordIndex + 2); // +2 to include potential punctuation
    }
  }

  // Join tokens with proper spacing (no space before punctuation)
  let result = '';
  for (let i = 0; i < selectedTokens.length; i++) {
    const token = selectedTokens[i];
    if (i === 0) {
      result = token;
    } else if (/^[.!?]$/.test(token)) {
      // Punctuation - no space before
      result += token;
    } else {
      // Regular word - add space before
      result += ' ' + token;
    }
  }

  return result.trim();
};

export const transcriptToMemory = (t: Transcript, calendarEvent?: CalendarEvent): Memory => {
  let date: Date;
  let endDate: Date;
  let time: string;
  let endTime: string;

  if (calendarEvent) {
    date = new Date(calendarEvent.start as string);
    time = formatTime(date);
    endDate = new Date(calendarEvent.end as string);
    endTime = formatTime(endDate);
  } else if (isTranscriptFull(t) && t.meeting?.date) {
    date = new Date(t.meeting.date);
    time = formatTime(date);
    endDate = new Date(date);
    endDate.setMinutes(endDate.getMinutes() + Math.max(t.duration || 1800, 1800) / 60);
    endTime = formatTime(endDate);
  } else {
    // Fallback if no meeting date is available - use transcript creation date or current date
    date = t.createdAt ? new Date(t.createdAt) : new Date();
    time = formatTime(date);
    endDate = new Date(date);
    endDate.setMinutes(endDate.getMinutes() + Math.max(t.duration || 1800, 1800) / 60);
    endTime = formatTime(endDate);
  }

  const title = t.name || calendarEvent?.title || '';

  const partialCalendarEvent = {
    start: date.toISOString(),
    end: endDate.toISOString(),
    ...calendarEvent,
    title
  } as Partial<CalendarEvent>;

  return {
    id: t.id,
    name: title,
    time: time,
    endTime,
    datetime: date.toISOString(),
    href: '#',
    calendarEvent: partialCalendarEvent,
    workspaceId: t.workspaceId,
    transcriptId: t.id,
    transcript: t,
    source: 'recording',
    dateStrLocal: getDateStrLocal(date)
  };
};

export const isTranscriptSummary = (
  transcript: Transcript | null | undefined
): transcript is TranscriptSummary => transcript?._type === 'transcriptSummary';

export const isTranscriptFull = (
  transcript: Transcript | null | undefined
): transcript is TranscriptFull => transcript?._type === 'fullTranscript';

export const isMediaFileLike = (value: unknown): value is IMediaFile => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return typeof candidate.path === 'string' && typeof candidate.assetUrl === 'string';
};

export const isStoredMediaFileLike = (value: unknown): value is StoredMediaFile => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return typeof candidate.originalPath === 'string' && typeof candidate.url === 'string';
};

export const demoteFullTranscriptToSummary = (transcript: TranscriptFull): TranscriptSummary => {
  // Remove heavy fields for in-memory store optimization
  // Note: This is only for the in-memory idMap, SQLite saves the full transcript

  const { meeting, ...rest } = transcript;
  return {
    ...rest,
    _type: 'transcriptSummary'
  };
};

// Helper function to parse time string (HH:MM:SS.mmm format)
// Exported for testing purposes
export function parseTimeString(timeStr: string): { seconds: number; timeData: TimeData } {
  const parts = timeStr.trim().split(':');
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  const secondsParts = parts[2].split('.');
  const seconds = parseInt(secondsParts[0], 10);
  const milliseconds = parseInt((secondsParts[1] || '0').padEnd(3, '0'), 10);

  const totalSeconds = hours * 3600 + minutes * 60 + seconds + milliseconds / 1000;

  const timeData: TimeData = {
    hours,
    minutes,
    seconds,
    hoursStr: String(hours).padStart(2, '0'),
    minutesStr: String(minutes).padStart(2, '0'),
    secondsStr: String(seconds).padStart(2, '0')
  };

  return { seconds: totalSeconds, timeData };
}

// Parse VTT format into transcript output lines
// Supports both standard WebVTT and dash-separated formats
// Exported for testing purposes
export function parseVttFormat(vttText: string): TranscriptOutputLineParsedTime[] {
  const lines = vttText
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line);
  const output: TranscriptOutputLineParsedTime[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Skip WEBVTT header and NOTE lines
    if (line.includes('WEBVTT') || line.startsWith('NOTE') || line === '') {
      i++;
      continue;
    }

    // Look for timestamp line (format: HH:MM:SS.mmm --> HH:MM:SS.mmm)
    // Can be followed by text on same line (dash-separated) or on next line (standard WebVTT)
    if (line.includes('-->')) {
      // Check if this line has both timestamp and text (dash-separated format)
      const dashMatch = line.match(/^(.+?-->.+?)[-\s]+(.+)$/);

      let timePartStr: string;
      let inlineText: string | null = null;

      if (dashMatch) {
        // Dash-separated format: "HH:MM:SS.mmm --> HH:MM:SS.mmm- text content"
        timePartStr = dashMatch[1];
        inlineText = dashMatch[2];
      } else {
        // Standard format: just the timestamp
        timePartStr = line;
      }

      const parts = timePartStr.split('-->');
      if (parts.length !== 2) {
        i++;
        continue;
      }

      const { seconds: startSeconds, timeData: startTimeData } = parseTimeString(parts[0]);
      const { seconds: endSeconds, timeData: endTimeData } = parseTimeString(parts[1]);

      // Collect text content from inline text or following lines
      let text = inlineText || '';
      let speaker: string | undefined;
      let j = i + 1;

      // If no inline text, look for text on following lines
      if (!inlineText) {
        while (j < lines.length && !lines[j].includes('-->')) {
          if (lines[j]) {
            // Remove speaker prefix if it exists (format "Speaker: text" or "Speaker - text")
            // Only match if colon or dash immediately follows the speaker name to avoid matching regular text
            let contentLine = lines[j];
            const speakerMatch = contentLine.match(/^([A-Za-z][A-Za-z0-9\s]*?)[:\-]\s*(.+)$/);

            if (speakerMatch && !contentLine.includes('-->') && !speaker) {
              // Only extract speaker from the first line that has it
              speaker = speakerMatch[1].trim();
              contentLine = speakerMatch[2].trim();
            }

            if (text) text += ' ';
            text += contentLine;
          }
          j++;
        }
      } else {
        // If we had inline text, no need to skip any lines
        j = i + 1;
      }

      if (text) {
        const vttLine = `${parts[0].trim()} --> ${parts[1].trim()}`;
        output.push({
          delta: endSeconds - startSeconds,
          vtt: vttLine,
          text: text.trim(),
          startSeconds,
          endSeconds,
          start: startTimeData,
          end: endTimeData,
          speaker: speaker as SegmentResult['dominantSource'] | undefined
        });
      }

      i = j;
    } else {
      i++;
    }
  }

  return output;
}

// Create a Memory object from VTT data
export function createMemoryFromVtt(
  vttText: string,
  workspaceId: string | undefined,
  userId: string
): Memory | null {
  console.log('Creating from vtt input, length:', vttText.length);
  try {
    const output = parseVttFormat(vttText);

    if (output.length === 0) {
      const errorMsg =
        'No transcript lines found in VTT input. Please ensure your VTT has at least one timestamp block.';
      console.warn('[VTT Parser] Not a valid VTT format:', errorMsg);
      console.warn('[VTT Input]', vttText);
      throw new Error(errorMsg);
    }

    console.log('[VTT Parser Success] Parsed', output.length, 'transcript lines');

    const now = new Date();
    const transcriptId = `manual-${Date.now()}`;
    const memoryId = `memory-${Date.now()}`;

    // Create transcript object
    const transcript: TranscriptFull = {
      _type: 'fullTranscript',
      id: transcriptId,
      name: 'Manual VTT Transcript',
      status: 'ready',
      fileExists: false,
      isDeleted: false,
      workspaceId: workspaceId || undefined,
      date: now,
      createdAt: now,
      updatedAt: now,
      userId: userId,
      rawOutput: output.map((line) => line.text),
      editedOutput: output.map((line) => line.text),
      output: output,
      meeting: null,
      duration: output[output.length - 1]?.endSeconds || 0
    };

    // Create memory object
    const memory: Memory = {
      id: memoryId,
      name: 'Manual VTT Memory',
      time: output[0]?.start.hoursStr + ':' + output[0]?.start.minutesStr || '00:00',
      endTime:
        output[output.length - 1]?.end.hoursStr + ':' + output[output.length - 1]?.end.minutesStr ||
        '00:00',
      datetime: now.toISOString(),
      href: `/memory/${memoryId}`,
      transcriptId: transcriptId,
      workspaceId: workspaceId || undefined,
      calendarEvent: {},
      source: 'recording',
      dateStrLocal: now.toLocaleDateString(),
      transcript: transcript
    };

    console.log('[VTT Parser] Created memory:', memoryId, 'with', output.length, 'lines');
    return memory;
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error);
    console.warn('[VTT Parser] Failed to parse as VTT format:', errorMsg);
    return null;
  }
}
